import React from "react";
import { useTranslation } from "react-i18next";
// Reuse the transaction list item styling so the list matches Transaction History.
import styles from "../../history/components/transaction-list-item/transaction-list-item.module.scss";
import paymentStyles from "./historical-payment.module.scss";
import { VendingTransactionHistory, BillingTransactionHistory } from "../../../../assets/icons";
import { formatCurrency } from "../../../../utils";
import { formatDate, formatTime } from "../../history/history.utils";
import { StatementOfAccountTransaction } from "../../../../model/vending/vending-interfaces";
import { ConsumerType } from "../../../../model/energy-profile";
import { transactionTypeLabel, transactionMoney } from "../historical-payment.utils";

interface PaymentHistoryListItemProps {
	transaction: StatementOfAccountTransaction;
	consumerType: ConsumerType | undefined;
	onClick?: (transaction: StatementOfAccountTransaction) => void;
}

export const PaymentHistoryListItem: React.FC<PaymentHistoryListItemProps> = ({ transaction, consumerType, onClick }) => {
	const { t } = useTranslation();
	// Icon + badge style follow the meter type; the label text follows the transaction type.
	const isPostpaid = consumerType === ConsumerType.Postpaid;
	const Icon = isPostpaid ? BillingTransactionHistory : VendingTransactionHistory;
	const label = transactionTypeLabel(transaction.transactionType, consumerType, t);
	const money = transactionMoney(transaction, consumerType);
	const date = `${formatDate(transaction.transactionDate)}, ${formatTime(transaction.transactionDate).toLocaleLowerCase()}`;

	// Postpaid rows have no detail endpoint: render a plain, non-interactive div (default cursor).
	const clickable = !!onClick;
	const Wrapper: React.ElementType = clickable ? "button" : "div";

	return (
		<Wrapper
			className={`${paymentStyles.transactionListItem} ${clickable ? "" : paymentStyles.static}`}
			onClick={clickable ? () => onClick?.(transaction) : undefined}
			role={clickable ? "button" : undefined}
			tabIndex={clickable ? 0 : undefined}
		>
			<div className={styles.iconContainer}>
				<Icon />
			</div>

			<div className={styles.content}>
				<div className={styles.leftSection}>
					<p className={styles.title}>{money ? formatCurrency(money.amount, { currency: money.currency }) : label}</p>
					<p className={styles.title}>{transaction.description ?? label}</p>
					<p className={styles.date}> {date}</p>
				</div>

				<div className={styles.rightSection}>
					<span className={`${paymentStyles.badge} ${isPostpaid ? paymentStyles.billing : paymentStyles.vending}`}>{label}</span>
				</div>
			</div>
		</Wrapper>
	);
};
